import React from 'react';
import { Table, TableHead, TableRow, TableCell, Typography, IconButton } from '@mui/material';
import { styled } from '@mui/material/styles';
import { TableVirtuoso } from 'react-virtuoso';
import { MdOutlineEditCalendar } from 'react-icons/md';
import { LocalAtRecord } from '../../db';
import { jobsSortingFunction } from '../../utils';
import { JobsFieldSet } from '@rogoag/airtable/crm';

interface JobAndDropoffListProps {
  list: LocalAtRecord[];
  updateZoomField: (job: LocalAtRecord) => void;
  onRolloverClick?: (job: LocalAtRecord) => void;
}

const CustomTable = styled(Table)({
  '& td, & th': {
    padding: '10px',
  },
});

// Wrap in forwardRef so react-virtuoso can attach a ref
const VirtuosoTable = React.forwardRef<HTMLTableElement, React.ComponentProps<typeof CustomTable>>((props, ref) => (
  <CustomTable {...props} ref={ref} style={{ maxWidth: '100%', width: '100%', tableLayout: 'fixed' }} />
));

const VirtuosoTableHead = React.forwardRef<HTMLTableSectionElement, React.ComponentProps<typeof TableHead>>(
  (props, ref) => <TableHead {...props} ref={ref} style={{ width: '100%', maxWidth: '100%' }} />,
);

export default function JobAndDropoffList(props: JobAndDropoffListProps) {
  // avoid mutating props.list
  const list = React.useMemo(() => [...props.list].sort(jobsSortingFunction), [props.list]);

  return (
    <div style={{ height: '100vh', width: '100%' }}>
      <TableVirtuoso
        data={list}
        style={{ height: '100%', width: '100%' }}
        components={{
          Table: VirtuosoTable,
          TableHead: VirtuosoTableHead,
        }}
        fixedHeaderContent={() => (
          <TableRow style={{ width: '100%', border: 0 }}>
            <TableCell>
              <Typography>
                <b>Late</b>
              </Typography>
            </TableCell>

            <TableCell>
              <Typography>
                <b>Samples</b>
              </Typography>
            </TableCell>

            <TableCell>
              <Typography>
                <b>Acres</b>
              </Typography>
            </TableCell>

            <TableCell>
              <Typography>
                <b>Grid</b>
              </Typography>
            </TableCell>
          </TableRow>
        )}
        /* IMPORTANT: itemContent must return TableCell(s) only — not TableRow */
        itemContent={(index, item: LocalAtRecord<JobsFieldSet>) => {
          const isJob = item.table === 'Jobs';

          return (
            /* single TableCell that spans header columns and contains stacked layout */
            <TableCell colSpan={4} style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  cursor: 'pointer',
                }}
                onClick={() => props.updateZoomField(item)}
              >
                <div>{`${isJob ? 'Job' : 'Dropoff'} -- ${item.get('Name')}`}</div>

                {isJob && props.onRolloverClick && (
                  <IconButton
                    size="small"
                    style={{ maxHeight: 20 }}
                    onClick={(event) => {
                      event.stopPropagation();

                      if (props.onRolloverClick) {
                        props.onRolloverClick(item);
                      }
                    }}
                  >
                    <MdOutlineEditCalendar color="#8594e4" />
                  </IconButton>
                )}
              </div>
              <div style={{ display: 'flex', marginTop: 8, gap: 12, justifyContent: 'space-between' }}>
                <div style={{ flex: 1, textAlign: 'center' }}>
                  {isJob && item.get('Late (days)') ? item.get('Late (days)') : 'DNE'}
                </div>

                <div style={{ flex: 1, textAlign: 'center' }}>
                  {isJob && item.get('# Samples') ? item.get('# Samples') : 'DNE'}
                </div>

                <div style={{ flex: 1, textAlign: 'center' }}>
                  {isJob && item.get('Boundary Acres')
                    ? Math.round(Number(item.get('Boundary Acres')! * 100) / 100)
                    : 'DNE'}
                </div>

                <div style={{ flex: 1, textAlign: 'center' }}>
                  {isJob && item.get('Grid') ? item.get('Grid') : 'DNE'}
                </div>
              </div>
            </TableCell>
          );
        }}
      />
    </div>
  );
}
